<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppSvgIcon, AppButton, AppLinkButton } from '@libs/ui';

import CarTripMap from '../CarTripMap.vue';

interface CollapsedCarTripSegmentsProps {
  segments: EditTripSegment[];
}

const props = defineProps<CollapsedCarTripSegmentsProps>();

interface CollapsedCarTripSegmentsEmits {
  (event: 'expand-panel'): void;
  (event: 'open-add-destination-step'): void;
}
const emit = defineEmits<CollapsedCarTripSegmentsEmits>();

const mapStore = useMapStore();

const { t } = i18n.global;

const MIDDLE_SEGMENTS_LIMIT = 3;
const START_AND_END_SEGMENTS = 2;

const startingLocationSegment = ref<EditTripSegment>();
const draggableSegments = ref<EditTripSegment[]>([]);

const showMiddleSegments = computed(
  () => draggableSegments.value.length >= MIDDLE_SEGMENTS_LIMIT
);
const lastSegment = computed(() => draggableSegments.value.at(-1));
const middleSegmentsCount = computed(() => draggableSegments.value.length - 1);

function updateSegments(): void {
  draggableSegments.value = cloneDeep(props.segments.slice(1, -1));
  startingLocationSegment.value = cloneDeep(props.segments[0]);
}

function onCollapsedSegmentsClicked(): void {
  if (props.segments.length > START_AND_END_SEGMENTS) {
    emit('expand-panel');
  }
}

watch(() => props.segments, updateSegments, { immediate: true });
</script>

<template>
  <div class="collapsed-car-trip-segments-wrapper">
    <div
      class="collapsed-car-trip-segments"
      :class="{ clickable: props.segments.length > START_AND_END_SEGMENTS }"
      @click="onCollapsedSegmentsClicked"
    >
      <div v-if="startingLocationSegment" class="first-location">
        <AppSvgIcon name="locationMark" />
        <span>{{ startingLocationSegment.destinationName }}</span>
      </div>

      <div v-if="props.segments.length > START_AND_END_SEGMENTS" class="line" />

      <template v-if="showMiddleSegments">
        <div class="segment">
          <AppSvgIcon name="locationMark" />
          <span class="destination">
            {{ middleSegmentsCount }}
            {{
              $t(
                'planner.configuration_panel.tabs.destinations'
              ).toLocaleLowerCase()
            }}
          </span>
        </div>
        <div class="line" />
        <div v-if="lastSegment" class="segment">
          <AppSvgIcon name="locationMark" />
          <span class="destination">{{ lastSegment.destinationName }}</span>
        </div>
      </template>

      <template v-else>
        <div v-for="(segment, index) in draggableSegments" :key="index">
          <div class="segment">
            <AppSvgIcon name="locationMark" />
            <span class="destination">{{ segment.destinationName }}</span>
          </div>
          <div v-if="index !== draggableSegments.length - 1" class="line" />
        </div>
      </template>
    </div>
    <div
      v-if="props.segments.length <= START_AND_END_SEGMENTS"
      class="collapsed-car-trip-segments clickable"
      @click="emit('open-add-destination-step')"
    >
      <div class="first-destination">
        <div class="grey-line" />
        <AppSvgIcon name="lookingGlass" />
        {{ $t('planner.where_to_travel') }}
      </div>
    </div>
    <div
      v-if="props.segments.length > START_AND_END_SEGMENTS"
      class="actions-wrapper"
    >
      <AppButton
        class="add-destination-button"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{ name: 'plus' }"
        @click="emit('open-add-destination-step')"
      >
        {{ $t('planner.add_destination') }}
      </AppButton>
      <AppLinkButton
        :text="t('planner.see_details')"
        size="large"
        right-icon="chevronDown"
        @click="emit('expand-panel')"
      />
    </div>
    <CarTripMap
      class="map-wrapper"
      :map-properties="mapStore.mapProperties"
      :should-load-map="mapStore.isMapLoading"
      @set-dynamic-map-integration="mapStore.setDynamicMapIntegration"
    />
  </div>
</template>

<style lang="scss" scoped>
.collapsed-car-trip-segments-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  height: 100%;
  .collapsed-car-trip-segments {
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    border: 1px solid var(--color-scrollbar);
    background: var(--color-white);
    padding: var(--spacing-12) var(--spacing-24);
    position: relative;
    gap: var(--spacing-4);
    &.clickable {
      cursor: pointer;
    }
    @include font-label-l-bold;

    .first-location,
    .segment {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
      .destination {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .first-destination {
      display: flex;
      align-items: center;
      color: var(--color-neutral-60);
      gap: var(--spacing-12);

      .grey-line {
        content: '';
        position: absolute;
        top: calc(var(--spacing-16) * -1);
        left: var(--spacing-64);
        width: 0;
        height: 14px;
        border: 1px dashed var(--color-neutral-60);
      }
    }
  }

  .line {
    height: var(--spacing-16);
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 11px;
      width: 0;
      height: 100%;
      border: 1px dashed var(--color-neutral-70);
    }
  }
  .actions-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
